import React, { useState, useEffect, useCallback } from 'react';
import { useApp } from '../context';
import {
  notificationsApi,
  remindersApi,
  dashboardApi,
  ApiReminder,
  ApiDashboard,
} from '../lib/api';
import { getCached, setCached } from '../lib/offlineStore';
import { registerCacheCleanup, hasChanged } from '../lib/moduleCache';
import { calculateHealthScore } from '../lib/healthScore';
import {
  Heart,
  Bell,
  Zap,
  Star,
  ArrowRight,
  BatteryMedium,
  Target,
  Calendar,
  Sparkles,
  AlertCircle,
  CheckCircle,
  X,
  UserPlus,
  Cake,
  PartyPopper,
  MessageCirclePlus,
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { Link, useNavigate } from 'react-router-dom';
import { getCommIcon, getCommHref } from '../lib/communicationHelpers';
import { usePageAnimation } from '../lib/usePageAnimation';
import ContactAvatar from './ContactAvatar';
import AnimatedNumber from './AnimatedNumber';

function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

function BirthdayDateIcon({ date }: { date: Date | string }) {
  const d = typeof date === 'string' ? new Date(date) : date;
  const day = d.getDate();
  const month = d.toLocaleDateString('en-US', { month: 'short' }).toUpperCase();
  return (
    <div className="flex flex-col items-center gap-0.5">
      <Calendar size={18} />
      <span className="text-[8px] font-bold leading-none">
        {day} {month}
      </span>
    </div>
  );
}

// Module-level cache so re-mounts don't flash skeletons
let _dashboardCache: ApiDashboard | null = null;
let _remindersCache: ApiReminder[] | null = null;
registerCacheCleanup(() => {
  _dashboardCache = null;
  _remindersCache = null;
});

export default function Dashboard() {
  const { state, setPendingInteraction } = useApp();
  const navigate = useNavigate();
  const shouldAnimate = usePageAnimation();

  const isAuth = !state.isGuestMode;

  const [snoozingCardId, setSnoozingCardId] = useState<string | null>(null);
  const [successCardId, setSuccessCardId] = useState<string | null>(null);
  const [hiddenCardIds, setHiddenCardIds] = useState<string[]>([]);

  // ─── API Dashboard data (auth only) ────────────────────────────────────────
  const [apiDashboard, setApiDashboard] = useState<ApiDashboard | null>(_dashboardCache);

  // ─── API Reminders (auth only) ──────────────────────────────────────────────
  const [apiReminders, setApiReminders] = useState<ApiReminder[]>(_remindersCache || []);

  // Show skeleton only on first load when no cached data exists
  const [minLoadElapsed, setMinLoadElapsed] = useState(!isAuth || !!_dashboardCache);

  useEffect(() => {
    if (!isAuth || state.syncPending) return;

    // Only show minimum skeleton time if we have no data at all
    let timer: ReturnType<typeof setTimeout> | undefined;
    if (!_dashboardCache) {
      timer = setTimeout(() => setMinLoadElapsed(true), 1000);
    }

    // Load from IDB cache first for instant render (skip if module cache exists)
    if (!_dashboardCache) {
      Promise.all([
        getCached<ApiDashboard>('dashboard'),
        getCached<ApiReminder[]>('reminders'),
      ]).then(([cachedDashboard, cachedReminders]) => {
        if (cachedDashboard) {
          setApiDashboard(cachedDashboard.data);
          _dashboardCache = cachedDashboard.data;
        }
        if (cachedReminders) {
          setApiReminders(cachedReminders.data);
          _remindersCache = cachedReminders.data;
        }
      });
    }

    // Then fetch fresh data from API in background — only update state if data changed
    Promise.all([
      dashboardApi.get().catch(() => null),
      remindersApi.list({ limit: 10 }).catch(() => null),
    ]).then(([dashboard, reminders]) => {
      if (dashboard) {
        if (hasChanged(_dashboardCache, dashboard)) setApiDashboard(dashboard);
        _dashboardCache = dashboard;
        setCached('dashboard', dashboard);
      }
      if (reminders) {
        if (hasChanged(_remindersCache, reminders.data)) setApiReminders(reminders.data);
        _remindersCache = reminders.data;
        setCached('reminders', reminders.data);
      }
    });
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [isAuth, state.syncPending, state.user?.birthday]);

  // Map duration label → days
  const durationToDays: Record<string, number> = {
    '1 Day': 1,
    '1 Week': 7,
    '1 Month': 30,
  };

  const handleSnoozeClick = (e: React.MouseEvent, id: string) => {
    e.preventDefault();
    setSnoozingCardId(id);
  };

  const handleSnoozeSelect = useCallback(
    (e: React.MouseEvent, id: string, duration: string) => {
      e.preventDefault();
      setSuccessCardId(id);

      // Call API for auth users
      if (isAuth) {
        const days = durationToDays[duration] || 7;
        if (id.startsWith('reminder-')) {
          const reminderId = Number(id.replace('reminder-', ''));
          remindersApi.snooze(reminderId, days).catch(() => {});
        } else if (id.startsWith('birthday-')) {
          const contactId = Number(id.replace('birthday-', ''));
          remindersApi.snoozeBirthday(contactId, days).catch(() => {});
        }
      }

      setTimeout(() => {
        if (id.startsWith('reminder-')) {
          const reminderId = Number(id.replace('reminder-', ''));
          setApiReminders((prev) => prev.filter((r) => r.id !== reminderId));
        }
        setHiddenCardIds((prev) => [...prev, id]);
        setSnoozingCardId(null);
        setSuccessCardId(null);
      }, 1500);
    },
    [isAuth],
  );

  const handleCancelSnooze = (e: React.MouseEvent) => {
    e.preventDefault();
    setSnoozingCardId(null);
  };

  // Unread notification count from API (falls back to local overdue check for guest)
  const [hasUnreadNotifications, setHasUnreadNotifications] = useState(false);
  useEffect(() => {
    if (state.isGuestMode) {
      setHasUnreadNotifications(state.contacts.some((c) => c.status === 'overdue'));
      return;
    }
    notificationsApi
      .unreadCount()
      .then(({ count }) => setHasUnreadNotifications(count > 0))
      .catch(() => {});
  }, [state.isGuestMode, state.contacts]);

  const quickWins = state.contacts.filter((c) => c.status === 'needs_attention').slice(0, 3);
  const innerCircle = state.contacts.filter((c) => c.innerCircle).slice(0, 4);

  // Weekly Goal Calculation
  const now = new Date();
  const startOfWeek = new Date(now);
  const dayOfWeek = now.getDay(); // 0=Sun, 1=Mon, ...
  const daysSinceMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  startOfWeek.setDate(now.getDate() - daysSinceMonday);
  startOfWeek.setHours(4, 0, 0, 0); // Monday 4 AM reset

  const localWeeklyInteractions = state.interactions.filter((i) => {
    const iDate = new Date(i.date);
    return iDate >= startOfWeek;
  });

  const target = state.weeklyGoalTarget || 5;
  const weeklyProgress = Math.min(
    apiDashboard ? apiDashboard.weeklyStats.interactionsThisWeek : localWeeklyInteractions.length,
    target,
  );
  const weeklyPercentage = (weeklyProgress / target) * 100;

  // Friendship health: prefer API dashboard data
  const friendshipHealth = apiDashboard
    ? apiDashboard.overallHealthScore
    : (() => {
        const hasHealthData =
          state.contacts.length > 0 && state.contacts.some((c) => c.healthScore >= 0);
        if (!hasHealthData) return null;
        const scoredContacts = state.contacts.filter((c) => c.healthScore >= 0);
        return Math.round(
          scoredContacts.reduce((sum, c) => sum + c.healthScore, 0) / scoredContacts.length,
        );
      })();

  // Compute upcoming birthdays (sorted by nearest)
  const upcomingBirthdays = React.useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return state.contacts
      .filter((c) => c.birthday)
      .map((contact) => {
        const [yearStr, month, day] = contact.birthday!.split('-');
        const birthYear = parseInt(yearStr, 10);
        const bday = new Date(today.getFullYear(), parseInt(month) - 1, parseInt(day));
        if (bday < today) bday.setFullYear(today.getFullYear() + 1);
        const daysUntil = Math.ceil((bday.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
        const turningAge = birthYear >= 1900 ? bday.getFullYear() - birthYear : null;
        return { contact, date: bday, daysUntil, turningAge };
      })
      .sort((a, b) => a.daysUntil - b.daysUntil);
  }, [state.contacts]);

  const upcomingMilestone = upcomingBirthdays.find((b) => b.daysUntil <= 30) ?? null;

  // Check if today is the user's birthday
  const userBirthday = React.useMemo(() => {
    const bday = state.user?.birthday;
    if (!bday) return null;
    const today = new Date();
    const [yearStr, month, day] = bday.split('-');
    const birthYear = parseInt(yearStr, 10);
    if (today.getMonth() + 1 === parseInt(month) && today.getDate() === parseInt(day)) {
      const turningAge = birthYear >= 1900 ? today.getFullYear() - birthYear : null;
      return { turningAge };
    }
    return null;
  }, [state.user?.birthday]);

  // Smart priority nudge: user birthday > contact birthday today > overdue/needs_attention > all good
  const priorityNudge = React.useMemo(() => {
    // User's own birthday is THE highest priority
    if (userBirthday)
      return { type: 'user_birthday' as const, turningAge: userBirthday.turningAge };

    if (state.contacts.length === 0) return { type: 'no_contacts' as const };

    // Auth users: wait for API data AND minimum display time before deciding
    if (isAuth && (!apiDashboard || !minLoadElapsed)) return { type: 'loading' as const };

    // Contact birthday today is next highest priority
    const birthdayToday = upcomingBirthdays.find((b) => b.daysUntil === 0);
    if (birthdayToday) {
      return {
        type: 'birthday_today' as const,
        contact: birthdayToday.contact,
        turningAge: birthdayToday.turningAge,
      };
    }

    // Auth users: use API overdue contacts (snooze-aware, server-computed)
    if (apiDashboard && apiDashboard.overdueContacts.length > 0) {
      const top = apiDashboard.overdueContacts[0];
      return {
        type: 'overdue' as const,
        contact: {
          id: String(top.id),
          name: top.name,
          status: 'overdue' as const,
          overdueDays: top.daysSinceContact,
          photoUrl: top.photoUrl,
        },
      };
    }

    // Guest mode: derive from local state
    if (!isAuth) {
      const attentionContact = state.contacts
        .filter(
          (c) =>
            (c.status === 'overdue' || c.status === 'needs_attention') &&
            !hiddenCardIds.includes(c.id),
        )
        .sort((a, b) => b.overdueDays - a.overdueDays)[0];
      if (attentionContact) {
        return { type: 'overdue' as const, contact: attentionContact };
      }
    }

    // Low priority: contact with no interactions logged yet
    const noInteractions = state.contacts.find(
      (c) => !c.lastInteractionDate && !hiddenCardIds.includes(c.id),
    );
    if (noInteractions) {
      return { type: 'no_interactions' as const, contact: noInteractions };
    }

    // Everyone's good!
    return { type: 'all_good' as const };
  }, [
    state.contacts,
    upcomingBirthdays,
    hiddenCardIds,
    apiDashboard,
    isAuth,
    minLoadElapsed,
    userBirthday,
  ]);

  return (
    <div className="p-5 space-y-5 pb-32 bg-[#f8f5f5] min-h-screen">
      {/* Header */}
      <header className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="size-10 rounded-full bg-[#fa8585] flex items-center justify-center text-white shadow-sm">
            <Heart size={20} fill="white" />
          </div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">Nudge</h1>
        </div>
        <Link to="/notifications" className="relative">
          <button className="size-10 rounded-full flex items-center justify-center text-slate-600 border border-slate-200/50 hover:bg-slate-200/50 transition-colors">
            <Bell size={20} />
            {hasUnreadNotifications && (
              <span className="absolute top-2 right-2 block h-2 w-2 rounded-full bg-[#fa8585] ring-2 ring-white"></span>
            )}
          </button>
        </Link>
      </header>

      {/* Bento Grid */}
      <div className="grid grid-cols-2 gap-3">
        {/* Priority Nudge - Hero (Span 2) */}
        <motion.div
          initial={shouldAnimate ? { opacity: 0, y: 20 } : false}
          animate={{ opacity: 1, y: 0 }}
          className="col-span-2 bg-slate-900 rounded-[2.5rem] p-7 text-white relative overflow-hidden shadow-2xl h-56"
        >
          <AnimatePresence mode="wait">
            {priorityNudge.type === 'loading' ? (
              <motion.div
                key="loading"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15 }}
                className="relative z-10 animate-pulse h-full flex flex-col"
              >
                <div className="flex items-center gap-2 mb-3">
                  <div className="size-7 bg-slate-800 rounded-lg" />
                  <div className="h-2.5 w-24 bg-slate-800 rounded" />
                </div>
                <div className="h-6 w-48 bg-slate-800 rounded-xl mb-2" />
                <div className="h-3.5 w-56 bg-slate-800/60 rounded-lg mb-1.5" />
                <div className="h-3.5 w-40 bg-slate-800/40 rounded-lg" />
                <div className="flex-1" />
                <div className="h-10 w-36 bg-slate-800 rounded-2xl" />
              </motion.div>
            ) : priorityNudge.type === 'user_birthday' ? (
              <motion.div
                key="user_birthday"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="h-full"
              >
                <div className="relative z-10 flex flex-col items-center justify-center text-center h-full">
                  <div className="flex items-center gap-3 mb-3">
                    <PartyPopper size={28} className="text-amber-400" />
                    <Cake size={28} className="text-[#fa8585]" />
                    <PartyPopper size={28} className="text-amber-400 -scale-x-100" />
                  </div>
                  <h2 className="text-xl font-bold mb-1 leading-tight">Happy Birthday! 🎉</h2>
                  <p className="text-slate-400 text-sm mb-4 max-w-60 leading-relaxed mx-auto line-clamp-2">
                    {priorityNudge.turningAge
                      ? `You're turning ${priorityNudge.turningAge} today! Here's to another amazing year.`
                      : "Wishing you an amazing day! Here's to another wonderful year."}
                  </p>
                  <Link
                    to="/contacts"
                    className="inline-flex items-center gap-2 bg-[#fa8585] px-5 py-2.5 rounded-2xl font-bold text-sm hover:scale-105 transition-transform shadow-lg shadow-[#fa8585]/20"
                  >
                    Celebrate with friends <ArrowRight size={16} />
                  </Link>
                </div>
                <div className="absolute -right-12 -bottom-12 size-48 bg-[#fa8585]/10 rounded-full blur-3xl"></div>
                <div className="absolute -left-12 -top-12 size-48 bg-amber-400/10 rounded-full blur-3xl"></div>
              </motion.div>
            ) : priorityNudge.type === 'no_contacts' ? (
              <motion.div
                key="no_contacts"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="h-full"
              >
                <div className="relative z-10 flex flex-col items-center justify-center text-center h-full">
                  <div className="bg-slate-800 p-3 rounded-full mb-3 ring-8 ring-slate-800/50">
                    <Heart size={28} className="text-[#fa8585]" fill="currentColor" />
                  </div>
                  <h2 className="text-xl font-bold mb-1 leading-tight">Your Network Awaits</h2>
                  <p className="text-slate-400 text-sm mb-4 max-w-60 leading-relaxed mx-auto line-clamp-2">
                    Add your first contact to start getting smart nudges and tracking your
                    relationships.
                  </p>
                  <Link
                    to="/add-contact"
                    className="inline-flex items-center gap-2 bg-[#fa8585] px-5 py-2.5 rounded-2xl font-bold text-sm hover:scale-105 transition-transform shadow-lg shadow-[#fa8585]/20"
                  >
                    <UserPlus size={16} /> Add Contact
                  </Link>
                </div>
                <div className="absolute -right-12 -bottom-12 size-48 bg-[#fa8585]/10 rounded-full blur-3xl"></div>
                <div className="absolute -left-12 -top-12 size-48 bg-blue-500/10 rounded-full blur-3xl"></div>
              </motion.div>
            ) : priorityNudge.type === 'birthday_today' ? (
              <motion.div
                key="birthday_today"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="h-full"
              >
                <div className="relative z-10 flex flex-col h-full">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="bg-amber-400 p-1.5 rounded-lg text-slate-900">
                      <Sparkles size={16} />
                    </div>
                    <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                      Birthday Today
                    </span>
                  </div>
                  <h2 className="text-xl font-bold mb-1 leading-tight truncate pr-16">
                    {priorityNudge.contact.name.split(' ')[0]}
                  </h2>
                  <p className="text-slate-400 text-sm leading-relaxed line-clamp-2 pr-16">
                    {priorityNudge.turningAge
                      ? `Turning ${priorityNudge.turningAge} today! Send them a message.`
                      : "It's their birthday! Send them a message."}
                  </p>
                  <div className="flex-1" />
                  <Link
                    to={`/contact/${priorityNudge.contact.id}`}
                    className="inline-flex items-center gap-2 bg-[#fa8585] px-5 py-2.5 rounded-2xl font-bold text-sm hover:scale-105 transition-transform shadow-lg shadow-[#fa8585]/20 w-fit"
                  >
                    Send wishes <ArrowRight size={16} />
                  </Link>
                </div>
                <div className="absolute -right-8 -bottom-8 size-48 bg-[#fa8585]/10 rounded-full blur-3xl"></div>
                <div className="absolute right-8 top-1/2 -translate-y-1/2 opacity-10">
                  <Sparkles size={140} strokeWidth={1} />
                </div>
              </motion.div>
            ) : priorityNudge.type === 'overdue' ? (
              <motion.div
                key="overdue"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="h-full"
              >
                <div className="relative z-10 flex flex-col h-full">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="bg-amber-400 p-1.5 rounded-lg text-slate-900">
                      <Star size={16} fill="currentColor" />
                    </div>
                    <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                      Priority Nudge
                    </span>
                  </div>
                  <h2 className="text-xl font-bold mb-1 leading-tight truncate pr-16">
                    {priorityNudge.contact.name.split(' ')[0]}
                  </h2>
                  <p className="text-slate-400 text-sm leading-relaxed line-clamp-2 pr-16">
                    {priorityNudge.contact.status === 'needs_attention'
                      ? 'Could use some attention — a quick check-in goes a long way.'
                      : `It's been ${priorityNudge.contact.overdueDays} days. Time for a quick check-in!`}
                  </p>
                  <div className="flex-1" />
                  <Link
                    to={`/contact/${priorityNudge.contact.id}`}
                    className="inline-flex items-center gap-2 bg-[#fa8585] px-5 py-2.5 rounded-2xl font-bold text-sm hover:scale-105 transition-transform shadow-lg shadow-[#fa8585]/20 w-fit"
                  >
                    Connect now <ArrowRight size={16} />
                  </Link>
                </div>
                <div className="absolute -right-8 -bottom-8 size-48 bg-[#fa8585]/10 rounded-full blur-3xl"></div>
                <div className="absolute right-8 top-1/2 -translate-y-1/2 opacity-10">
                  <Heart size={140} strokeWidth={1} />
                </div>
              </motion.div>
            ) : priorityNudge.type === 'no_interactions' ? (
              <motion.div
                key="no_interactions"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="h-full"
              >
                <div className="relative z-10 flex flex-col h-full">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="bg-blue-400 p-1.5 rounded-lg text-white">
                      <MessageCirclePlus size={16} />
                    </div>
                    <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                      Get Started
                    </span>
                  </div>
                  <h2 className="text-xl font-bold mb-1 leading-tight truncate pr-16">
                    {priorityNudge.contact.name.split(' ')[0]}
                  </h2>
                  <p className="text-slate-400 text-sm leading-relaxed line-clamp-2 pr-4">
                    Log your first interaction to start tracking this relationship.
                  </p>
                  <div className="flex-1" />
                  <Link
                    to="/log"
                    state={{ contactId: priorityNudge.contact.id }}
                    className="inline-flex items-center gap-2 bg-blue-500 px-5 py-2.5 rounded-2xl font-bold text-sm hover:scale-105 transition-transform shadow-lg shadow-blue-500/20 w-fit"
                  >
                    Log interaction <ArrowRight size={16} />
                  </Link>
                </div>
                <div className="absolute -right-8 -bottom-8 size-48 bg-blue-500/10 rounded-full blur-3xl"></div>
                <div className="absolute right-8 top-1/2 -translate-y-1/2 opacity-10">
                  <MessageCirclePlus size={140} strokeWidth={1} />
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="all_good"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="h-full"
              >
                <div className="relative z-10 flex flex-col items-center justify-center text-center h-full">
                  <div className="bg-emerald-500/20 p-3 rounded-full mb-3 ring-8 ring-emerald-500/10">
                    <CheckCircle size={28} className="text-emerald-400" />
                  </div>
                  <h2 className="text-xl font-bold mb-1 leading-tight">You're all caught up!</h2>
                  <p className="text-slate-400 text-sm mb-4 max-w-60 leading-relaxed mx-auto line-clamp-2">
                    All your relationships are in great shape. Keep up the amazing work!
                  </p>
                  <Link
                    to="/contacts"
                    className="inline-flex items-center gap-2 bg-emerald-500 px-5 py-2.5 rounded-2xl font-bold text-sm hover:scale-105 transition-transform shadow-lg shadow-emerald-500/20"
                  >
                    View contacts <ArrowRight size={16} />
                  </Link>
                </div>
                <div className="absolute -right-12 -bottom-12 size-48 bg-emerald-500/10 rounded-full blur-3xl"></div>
                <div className="absolute -left-12 -top-12 size-48 bg-emerald-500/5 rounded-full blur-3xl"></div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Weekly Goal Card */}
        <motion.div
          initial={shouldAnimate ? { opacity: 0, scale: 0.9, z: 0 } : false}
          animate={{ opacity: 1, scale: 1, z: 0 }}
          transition={shouldAnimate ? { delay: 0.1 } : undefined}
          whileTap={{ scale: 0.95 }}
          onClick={() => navigate('/weekly-goal')}
          className="bg-white rounded-[2rem] p-4 shadow-sm border border-slate-100 flex items-center gap-3 cursor-pointer hover:border-blue-200 transition-colors relative z-10"
        >
          <div className="bg-blue-50 p-2 rounded-xl text-blue-500 shrink-0">
            <Target size={18} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline gap-1">
              <span className="text-2xl font-bold text-slate-900">{weeklyProgress}</span>
              <span className="text-slate-400 font-bold text-xs">/ {target}</span>
            </div>
            <div className="w-full bg-slate-100 h-1 rounded-full overflow-hidden mt-1.5">
              <div
                className="bg-blue-500 h-full rounded-full transition-all duration-1000"
                style={{ width: `${weeklyPercentage}%` }}
              ></div>
            </div>
            <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mt-1">
              Weekly Goal
            </p>
          </div>
        </motion.div>

        {/* Friendship Health Card */}
        <motion.div
          initial={shouldAnimate ? { opacity: 0, scale: 0.9, z: 0 } : false}
          animate={{ opacity: 1, scale: 1, z: 0 }}
          transition={shouldAnimate ? { delay: 0.2 } : undefined}
          className="bg-emerald-500 rounded-[2rem] p-4 text-white flex items-center gap-3 shadow-lg shadow-emerald-500/20 relative z-10"
        >
          <div className="bg-white/20 p-2 rounded-xl shrink-0">
            <BatteryMedium size={18} />
          </div>
          <div>
            <span className="text-2xl font-bold">
              {friendshipHealth !== null ? <AnimatedNumber value={friendshipHealth} /> : 'N/A'}
            </span>
            <p className="text-[9px] font-bold text-white/70 uppercase tracking-widest mt-0.5">
              Friendship Health
            </p>
          </div>
        </motion.div>

        {/* Quick Wins Full Width Card */}
        {quickWins.length > 0 && (
          <motion.div
            initial={shouldAnimate ? { opacity: 0, y: 20 } : false}
            animate={{ opacity: 1, y: 0 }}
            transition={shouldAnimate ? { delay: 0.3 } : undefined}
            className="col-span-2 bg-white rounded-[2.5rem] p-5 shadow-sm border border-slate-100"
          >
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <div className="bg-[#fa8585]/10 text-[#fa8585] p-2 rounded-xl">
                  <Zap size={20} />
                </div>
                <h3 className="text-lg font-bold text-slate-900">Quick Wins</h3>
              </div>
              <span className="bg-[#fa8585] text-white text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-wider">
                {quickWins.length} Available
              </span>
            </div>

            <div className="space-y-3">
              {quickWins.map((contact) => (
                <motion.div
                  key={contact.id}
                  whileTap={{ scale: 0.98 }}
                  className="bg-slate-50 rounded-2xl p-4 flex items-center gap-4 border border-transparent hover:border-slate-200 transition-all"
                >
                  <Link
                    to={`/contact/${contact.id}`}
                    className="shrink-0 ring-2 ring-white rounded-full"
                  >
                    <ContactAvatar photoUrl={contact.photoUrl} name={contact.name} size="sm" />
                  </Link>
                  <Link to={`/contact/${contact.id}`} className="flex-1 min-w-0">
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-tighter">
                      Send a text
                    </p>
                    <p className="font-bold text-slate-900 truncate">{contact.name}</p>
                  </Link>
                  <div className="flex gap-2">
                    {(() => {
                      const methods = [...(contact.communicationMethods || [])].sort((a, b) => {
                        if (a.type === contact.preferredCommMethod) return -1;
                        if (b.type === contact.preferredCommMethod) return 1;
                        return 0;
                      });

                      if (methods.length === 0) {
                        return null;
                      }

                      return methods.slice(0, 2).map((method, idx) => (
                        <a
                          key={idx}
                          href={getCommHref(method)}
                          onClick={(e) => {
                            e.stopPropagation();
                            setPendingInteraction({
                              contactId: contact.id,
                              type: method.type.toLowerCase() === 'phone' ? 'call' : 'text',
                            });
                          }}
                          className="size-9 rounded-xl bg-white text-slate-500 flex items-center justify-center shadow-sm hover:bg-slate-50 transition-colors"
                        >
                          {getCommIcon(method.type)}
                        </a>
                      ));
                    })()}
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Inner Circle Section (Span 2) */}
        {innerCircle.length > 0 && (
          <div className="col-span-2 pt-2">
            <div className="flex justify-between items-center mb-4 px-2">
              <h3 className="text-lg font-bold text-slate-900">Inner Circle</h3>
              <Link
                to="/contacts?filter=Inner Circle"
                className="text-xs font-bold text-[#fa8585] uppercase"
              >
                View All
              </Link>
            </div>
            <div className="grid grid-cols-4 gap-3">
              {innerCircle.map((contact, idx) => {
                // Compute real-time status so ring color stays fresh between daily cron runs
                const liveStatus = calculateHealthScore(
                  contact.lastInteractionDate || undefined,
                  contact.contactFrequencyDays,
                ).status;
                return (
                  <motion.div
                    key={contact.id}
                    initial={shouldAnimate ? { opacity: 0, y: 10 } : false}
                    animate={{ opacity: 1, y: 0 }}
                    transition={shouldAnimate ? { delay: 0.4 + idx * 0.1 } : undefined}
                    className="flex flex-col items-center gap-2"
                  >
                    <Link to={`/contact/${contact.id}`} className="relative group">
                      <div
                        className={`size-16 rounded-2xl overflow-hidden ring-2 ring-offset-2 transition-all group-hover:scale-105 ${
                          liveStatus === 'overdue'
                            ? 'ring-[#FC8181]'
                            : liveStatus === 'needs_attention'
                              ? 'ring-amber-400'
                              : liveStatus === 'new' || !contact.lastInteractionDate
                                ? 'ring-slate-300'
                                : 'ring-emerald-400'
                        }`}
                      >
                        <ContactAvatar
                          photoUrl={contact.photoUrl}
                          name={contact.name}
                          size="sm"
                          className="size-full! rounded-2xl! text-lg!"
                        />
                      </div>
                      <div className="absolute -bottom-1 -right-1 size-5 bg-white rounded-full shadow-sm flex items-center justify-center">
                        <div
                          className={`size-2.5 rounded-full ${
                            liveStatus === 'overdue'
                              ? 'bg-[#FC8181]'
                              : liveStatus === 'needs_attention'
                                ? 'bg-amber-500'
                                : liveStatus === 'new' || !contact.lastInteractionDate
                                  ? 'bg-slate-400'
                                  : 'bg-emerald-500'
                          }`}
                        ></div>
                      </div>
                    </Link>
                    <span className="text-[10px] font-bold text-slate-600 truncate w-full text-center">
                      {contact.name.split(' ')[0]}
                    </span>
                  </motion.div>
                );
              })}
            </div>
          </div>
        )}

        {/* Upcoming Milestone / Reminders — API-driven for auth, local for guest */}
        <div className="col-span-2 space-y-2">
          <AnimatePresence>
            {(() => {
              // Auth users: show API reminders + upcoming birthdays from dashboard
              if (isAuth) {
                const cards: React.ReactNode[] = [];
                let cardIdx = 0;

                // Reminders from API (overdue nudges, birthday-tomorrow reminders)
                for (const reminder of apiReminders) {
                  const cardId = `reminder-${reminder.id}`;
                  if (hiddenCardIds.includes(cardId)) continue;
                  const isBirthday = reminder.reminderType === 'birthday';
                  const bdayMatch = isBirthday
                    ? (apiDashboard?.upcomingBirthdays.find((b) => b.id === reminder.contactId) ??
                      null)
                    : null;
                  const localBday = isBirthday
                    ? upcomingBirthdays.find(
                        (b) => String(b.contact.id) === String(reminder.contactId),
                      )
                    : null;
                  const reminderTurningAge = bdayMatch?.turningAge ?? localBday?.turningAge ?? null;
                  const reminderDaysUntil = bdayMatch?.daysUntil ?? localBday?.daysUntil ?? null;
                  const reminderBirthdayDate = bdayMatch?.birthday
                    ? new Date(bdayMatch.birthday)
                    : (localBday?.date ?? null);
                  const idx = cardIdx++;
                  cards.push(
                    <motion.div
                      key={cardId}
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      transition={{ duration: 0.2, delay: idx * 0.06 }}
                      className="bg-white rounded-2xl p-3.5 shadow-sm border border-slate-100 flex items-center gap-3 relative overflow-hidden"
                    >
                      <Link
                        to={`/contact/${reminder.contactId}`}
                        className="flex flex-1 items-center gap-3 min-w-0"
                      >
                        <div
                          className={`size-10 rounded-xl flex items-center justify-center shrink-0 ${
                            isBirthday
                              ? 'bg-amber-50 text-amber-600'
                              : 'bg-[#fa8585]/10 text-[#fa8585]'
                          }`}
                        >
                          {isBirthday && reminderBirthdayDate ? (
                            <BirthdayDateIcon date={reminderBirthdayDate} />
                          ) : isBirthday ? (
                            <Calendar size={18} />
                          ) : (
                            <AlertCircle size={18} />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-bold text-sm text-slate-900 truncate">
                            {isBirthday
                              ? `${reminder.contactName?.split(' ')[0] || 'Their'}'s${reminderTurningAge ? ` ${ordinal(reminderTurningAge)}` : ''} birthday`
                              : reminder.title}
                          </h4>
                          <p className="text-xs text-slate-500 truncate">
                            {isBirthday && reminderDaysUntil !== null
                              ? reminderDaysUntil === 0
                                ? 'Today! • Send wishes'
                                : reminderDaysUntil === 1
                                  ? 'Tomorrow!'
                                  : `In ${reminderDaysUntil} days`
                              : (reminder.message ?? '')}
                          </p>
                        </div>
                      </Link>
                      <button
                        onClick={(e) => handleSnoozeClick(e, cardId)}
                        className="bg-slate-100 text-slate-500 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase active:scale-95 transition-transform shrink-0"
                      >
                        Snooze
                      </button>

                      {/* Snooze Overlay */}
                      <AnimatePresence>
                        {snoozingCardId === cardId && (
                          <motion.div
                            initial={{ x: '100%' }}
                            animate={{ x: 0 }}
                            exit={{ x: '100%' }}
                            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                            className="absolute inset-0 bg-white/95 backdrop-blur-sm flex items-center justify-between px-5 z-10"
                          >
                            {successCardId === cardId ? (
                              <div className="flex-1 flex items-center justify-center gap-2 text-[#68D391] font-bold">
                                <CheckCircle size={20} />
                                <span>Snoozed!</span>
                              </div>
                            ) : (
                              <>
                                <div className="flex gap-2">
                                  <button
                                    onClick={(e) => handleSnoozeSelect(e, cardId, '1 Day')}
                                    className="px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-sm font-bold transition-colors"
                                  >
                                    1 Day
                                  </button>
                                  <button
                                    onClick={(e) => handleSnoozeSelect(e, cardId, '1 Week')}
                                    className="px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-sm font-bold transition-colors"
                                  >
                                    1 Week
                                  </button>
                                  <button
                                    onClick={(e) => handleSnoozeSelect(e, cardId, '1 Month')}
                                    className="px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-sm font-bold transition-colors"
                                  >
                                    1 Month
                                  </button>
                                </div>
                                <button
                                  onClick={handleCancelSnooze}
                                  className="p-2 text-slate-400 hover:text-slate-600 transition-colors"
                                >
                                  <X size={20} />
                                </button>
                              </>
                            )}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>,
                  );
                }

                // Upcoming birthdays from dashboard API (within 30 days, not already shown as a reminder)
                const birthdayReminderContactIds = new Set(
                  apiReminders.filter((r) => r.reminderType === 'birthday').map((r) => r.contactId),
                );
                for (const bday of apiDashboard?.upcomingBirthdays ?? []) {
                  if (birthdayReminderContactIds.has(bday.id)) continue;
                  const cardId = `birthday-${bday.id}`;
                  if (hiddenCardIds.includes(cardId)) continue;
                  const bdayDate = new Date(bday.birthday);
                  const idx = cardIdx++;
                  cards.push(
                    <motion.div
                      key={cardId}
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      transition={{ duration: 0.2, delay: idx * 0.06 }}
                      className="bg-white rounded-2xl p-3.5 shadow-sm border border-slate-100 flex items-center gap-3 relative overflow-hidden"
                    >
                      <Link
                        to={`/contact/${bday.id}`}
                        className="flex flex-1 items-center gap-3 min-w-0"
                      >
                        <div className="size-10 rounded-xl bg-amber-50 flex items-center justify-center text-amber-600 shrink-0">
                          <BirthdayDateIcon date={bdayDate} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-bold text-sm text-slate-900 truncate">
                            {bday.name.split(' ')[0]}'s
                            {bday.turningAge ? ` ${ordinal(bday.turningAge)}` : ''} birthday
                          </h4>
                          <p className="text-xs text-slate-500">
                            {bday.daysUntil === 0
                              ? 'Today! • Send wishes'
                              : bday.daysUntil === 1
                                ? 'Tomorrow!'
                                : `In ${bday.daysUntil} days`}
                          </p>
                        </div>
                      </Link>
                      <button
                        onClick={(e) => handleSnoozeClick(e, cardId)}
                        className="bg-slate-100 text-slate-500 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase active:scale-95 transition-transform shrink-0"
                      >
                        Dismiss
                      </button>

                      {/* Snooze/Dismiss Overlay */}
                      <AnimatePresence>
                        {snoozingCardId === cardId && (
                          <motion.div
                            initial={{ x: '100%' }}
                            animate={{ x: 0 }}
                            exit={{ x: '100%' }}
                            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                            className="absolute inset-0 bg-white/95 backdrop-blur-sm flex items-center justify-between px-5 z-10"
                          >
                            {successCardId === cardId ? (
                              <div className="flex-1 flex items-center justify-center gap-2 text-[#68D391] font-bold">
                                <CheckCircle size={20} />
                                <span>Dismissed!</span>
                              </div>
                            ) : (
                              <>
                                <div className="flex gap-2">
                                  <button
                                    onClick={(e) => handleSnoozeSelect(e, cardId, '1 Day')}
                                    className="px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-sm font-bold transition-colors"
                                  >
                                    1 Day
                                  </button>
                                  <button
                                    onClick={(e) => handleSnoozeSelect(e, cardId, '1 Week')}
                                    className="px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-sm font-bold transition-colors"
                                  >
                                    1 Week
                                  </button>
                                  <button
                                    onClick={(e) => handleSnoozeSelect(e, cardId, '1 Month')}
                                    className="px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-sm font-bold transition-colors"
                                  >
                                    1 Month
                                  </button>
                                </div>
                                <button
                                  onClick={handleCancelSnooze}
                                  className="p-2 text-slate-400 hover:text-slate-600 transition-colors"
                                >
                                  <X size={20} />
                                </button>
                              </>
                            )}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>,
                  );
                }

                return cards;
              }

              // Guest mode: fall back to local upcoming birthday
              if (!upcomingMilestone || hiddenCardIds.includes('milestone-1')) return null;
              return (
                <motion.div
                  key="milestone-1"
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.2 }}
                  className="bg-white rounded-2xl p-3.5 shadow-sm border border-slate-100 flex items-center gap-3 relative overflow-hidden"
                >
                  <Link
                    to={`/contact/${upcomingMilestone.contact.id}`}
                    className="flex flex-1 items-center gap-3 min-w-0"
                  >
                    <div className="size-10 rounded-xl bg-amber-50 flex items-center justify-center text-amber-600 shrink-0">
                      <BirthdayDateIcon date={upcomingMilestone.date} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-bold text-sm text-slate-900 truncate">
                        {upcomingMilestone.contact.name.split(' ')[0]}'s
                        {upcomingMilestone.turningAge
                          ? ` ${ordinal(upcomingMilestone.turningAge)}`
                          : ''}{' '}
                        birthday
                      </h4>
                      <p className="text-xs text-slate-500">
                        {upcomingMilestone.daysUntil === 0
                          ? 'Today! • Send wishes'
                          : upcomingMilestone.daysUntil === 1
                            ? 'Tomorrow!'
                            : `In ${upcomingMilestone.daysUntil} days`}
                      </p>
                    </div>
                  </Link>
                  <button
                    onClick={(e) => handleSnoozeClick(e, 'milestone-1')}
                    className="bg-slate-100 text-slate-500 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase active:scale-95 transition-transform shrink-0"
                  >
                    Remind
                  </button>

                  {/* Snooze Overlay */}
                  <AnimatePresence>
                    {snoozingCardId === 'milestone-1' && (
                      <motion.div
                        initial={{ x: '100%' }}
                        animate={{ x: 0 }}
                        exit={{ x: '100%' }}
                        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                        className="absolute inset-0 bg-white/95 backdrop-blur-sm flex items-center justify-between px-5 z-10"
                      >
                        {successCardId === 'milestone-1' ? (
                          <div className="flex-1 flex items-center justify-center gap-2 text-[#68D391] font-bold">
                            <CheckCircle size={20} />
                            <span>Snoozed!</span>
                          </div>
                        ) : (
                          <>
                            <div className="flex gap-2">
                              <button
                                onClick={(e) => handleSnoozeSelect(e, 'milestone-1', '1 Day')}
                                className="px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-sm font-bold transition-colors"
                              >
                                1 Day
                              </button>
                              <button
                                onClick={(e) => handleSnoozeSelect(e, 'milestone-1', '1 Week')}
                                className="px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-sm font-bold transition-colors"
                              >
                                1 Week
                              </button>
                              <button
                                onClick={(e) => handleSnoozeSelect(e, 'milestone-1', '1 Month')}
                                className="px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-sm font-bold transition-colors"
                              >
                                1 Month
                              </button>
                            </div>
                            <button
                              onClick={handleCancelSnooze}
                              className="p-2 text-slate-400 hover:text-slate-600 transition-colors"
                            >
                              <X size={20} />
                            </button>
                          </>
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })()}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
